/// <amd-module name="Build/Queue-Panel/ResourcesControllerView" />
import * as React from "react";

import { BuildResourceData, ContainerResourceData, PipelineResourceData, RepositoryResourceData, PackageResourceData } from "Build/Client/Contracts/DataProvider";
import * as Resources from "Build/Queue-Panel/Resources";
import { QueueBuildStore } from "Build/Queue-Panel/Stores/QueueBuild";
import { ISourceProvider } from "Build/SourceProviders/Common";
import { ISourceProviderService } from "Build/SourceProviders/SourceProvider";
import { GitConstants } from "Build/Queue-Panel/Constants";
import { IPipelineResourcePanelProps, PipelineResourcePanel } from "Build/Queue-Panel/PipelineResourcePanel";
import { IBuildResourcePanelProps, BuildResourcePanel } from "Build/Queue-Panel/BuildResourcePanel";
import { IContainerResourcePanelProps, ContainerResourcePanel } from "Build/Queue-Panel/ContainerResourcePanel";
import { IRepositoryResourcePanelProps, RepositoryResourcePanel } from "Build/Queue-Panel/RepositoryResourcePanel";
import { IPackageResourcePanelProps, PackageResourcePanel } from "Build/Queue-Panel/PackageResourcePanel";
import { IObservableArray, IObservableValue, ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { equals, format } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssLayoutManager } from "VSS/Platform/Layout";

import { Icon, IconSize } from "VSSUI/Icon";
import { IListItemDetails, IListRow, List, ListItem, ListSelection } from "VSSUI/List";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Observer } from "VSSUI/Observer";
import { Panel } from "VSSUI/Panel";
import { Spinner } from "VSSUI/Spinner";

export interface IResourcesControllerViewProps {
    pageContext: IVssPageContext;
    store: QueueBuildStore;
    pipelineId: number;
    onDismiss: () => void;
}

type IResourceItem = PipelineResourceData | RepositoryResourceData | BuildResourceData | ContainerResourceData | PackageResourceData;

function isRepositoryResource(resource: PipelineResourceData | RepositoryResourceData | BuildResourceData | ContainerResourceData | PackageResourceData): resource is RepositoryResourceData {
    return (resource as RepositoryResourceData).refName !== undefined;
}

function isBuildResource(resource: PipelineResourceData | RepositoryResourceData | BuildResourceData | ContainerResourceData | PackageResourceData): resource is BuildResourceData {
    return (resource as BuildResourceData).buildType !== undefined;
}

function isContainerResource(resource: PipelineResourceData | RepositoryResourceData | BuildResourceData| ContainerResourceData | PackageResourceData): resource is ContainerResourceData {
    const containerResource = resource as ContainerResourceData
    return containerResource.containerType !== undefined && containerResource.properties !== undefined;
}

function isPackageResource(resource: PipelineResourceData | RepositoryResourceData | BuildResourceData| ContainerResourceData | PackageResourceData): resource is PackageResourceData {
    return (resource as PackageResourceData).packageType !== undefined;
}

export class ResourcesControllerView extends React.Component<IResourcesControllerViewProps> {
    private _isLoading: IObservableValue<boolean>;
    private _listSelection: ListSelection = new ListSelection();
    private _resources: IObservableArray<IResourceItem>;

    constructor(props: IResourcesControllerViewProps) {
        super(props);
        this._isLoading = new ObservableValue(false);
        this._resources = new ObservableArray([]);
    }

    public componentDidMount() {
        this._initializeResources();
    }

    public render(): JSX.Element {
        return (
            <Panel
                backButtonProps={{ onClick: this.props.onDismiss }}
                contentClassName="queue-panel-child no-padding"
                description={Resources.ResourcesDescriptionText}
                escDismiss={true}
                footerButtonProps={[
                    {
                        text: Resources.CloseButtonLabel,
                        onClick: this.props.onDismiss
                    }
                ]}
                onDismiss={this.props.onDismiss}
                titleProps={{ text: Resources.Resources }}
            >
                <div className="queue-panel-list-container custom-scrollbar">
                    <Observer loading={this._isLoading}>
                        {(props: { loading: boolean, resources: IResourceItem[]}) => props.loading
                            ? (<div><Spinner className="queue-build-spinner" /></div>)
                            : (
                                <Observer errorMessage={this.props.store.errorMessage} resources={this._resources}>
                                    {(props: { errorMessage: string, resources: IResourceItem[] }) => {
                                        if (props.errorMessage) {
                                            return (
                                                <div className="margin-vertical-8 padding-horizontal-20">
                                                    <MessageCard className="multiline-error" severity={MessageCardSeverity.Error}>{props.errorMessage}</MessageCard>
                                                </div>
                                            );
                                        }
                                        else if (props.resources.length > 0) {
                                            return (
                                                <List
                                                    itemProvider={this._resources}
                                                    renderRow={this._renderRow}
                                                    width="100%"
                                                    singleClickActivation={true}
                                                    selection={this._listSelection}
                                                    onActivate={this._editResource}
                                                    initialPageCount={10}
                                                />
                                            );
                                        }
                                        else {
                                            return (
                                                <div className="margin-vertical-8 padding-horizontal-20">
                                                    <MessageCard severity={MessageCardSeverity.Info}>{Resources.ResourcesNoResourcesText}</MessageCard>
                                                </div>
                                            );
                                        }
                                    }}
                                </Observer>
                            )}
                    </Observer>
                </div>
            </Panel>
        );
    }

    private _renderRow = (index: number, resource: IResourceItem, details: IListItemDetails<IResourceItem>): JSX.Element => {
        let name: string;
        let version: string;
        let icon: string;
        let refIcon: string | undefined;

        if (isRepositoryResource(resource)) {
            const isInlineSyntax = resource.alias.indexOf("://") > -1;
            name = isInlineSyntax ? resource.name : format("{0} [{1}]", resource.name, resource.alias);
            icon = this._getSourceProvider(resource).getRepoIconName();
            [version, refIcon] = this._getUpdatedRefTextAndIcon(resource.version || resource.refName);
        }
        else if (isBuildResource(resource)) {
            name = format("{0} [{1}]", resource.name, resource.alias);
            icon = "Build";
            version = resource.versionName || Resources.BuildResourceLastRun;
            refIcon = undefined;
        }
        else {
            name = format("{0} [{1}]", resource.name, resource.alias);
            icon = "Package";
            version = resource.version || Resources.PipelineResourceLastRun;
            refIcon = undefined;
        }

        return (
            <ListItem key={"list-item" + index} index={index} details={details}>
                <div className="queue-panel-list-row flex-grow flex-row">
                    <Icon iconName={icon} size={IconSize.medium} className="queue-panel-list-icon margin-right-8" />
                    <div className="flex-column justify-center margin-left-4">
                        <span className="text-ellipsis primary-text">{name}</span>
                        <span className="flex-center flex-row font-size secondary-text text-ellipsis">
                            {refIcon && <Icon iconName={refIcon} size={IconSize.small} className="margin-right-4" />}
                            <span>{version}</span>
                        </span>
                    </div>
                    <span className="flex-grow flex-row justify-end">
                        <Icon iconName="ChevronRightMed" size={IconSize.medium} />
                    </span>
                </div>
            </ListItem>
        );
    }

    private _editResource = (event: React.SyntheticEvent<HTMLElement, Event>, listRow: IListRow<RepositoryResourceData | PipelineResourceData>) => {
        const resource = listRow.data;
        if (isRepositoryResource(resource)) {
            this.layoutManager.renderCallout((onDismiss) => {
                return React.createElement(RepositoryResourcePanel, {
                    pageContext: this.props.pageContext,
                    resource,
                    onDismiss,
                    onUpdate: this.props.store.updateRepositoryResource
                } as IRepositoryResourcePanelProps);
            });
        }
        else if (isContainerResource(resource)) {
            this.layoutManager.renderCallout((onDismiss) => {
                return React.createElement(ContainerResourcePanel, {
                    pageContext: this.props.pageContext,
                    store: this.props.store,
                    resource,
                    onDismiss,
                    onUpdate: this.props.store.updateContainerResource
                } as IContainerResourcePanelProps);
            });
        }
        else if (isBuildResource(resource)) {
            this.layoutManager.renderCallout((onDismiss) => {
                return React.createElement(BuildResourcePanel, {
                    pageContext: this.props.pageContext,
                    store: this.props.store,
                    resource,
                    onDismiss,
                    onUpdate: this.props.store.updateBuildResource
                } as IBuildResourcePanelProps);
            });
        }
        else if (isPackageResource(resource)) {
            this.layoutManager.renderCallout((onDismiss) => {
                return React.createElement(PackageResourcePanel, {
                    pageContext: this.props.pageContext,
                    store: this.props.store,
                    resource,
                    onDismiss,
                    onUpdate: this.props.store.updatePackageResource
                } as IPackageResourcePanelProps);
            });
        }
        else {
            this.layoutManager.renderCallout((onDismiss) => {
                return React.createElement(PipelineResourcePanel, {
                    pageContext: this.props.pageContext,
                    store: this.props.store,
                    resource,
                    onDismiss,
                    onUpdate: this.props.store.updatePipelineResource
                } as IPipelineResourcePanelProps);
            });
        }
    };

    private _getSourceProvider(repositoryResource: RepositoryResourceData): ISourceProvider {
        const sourceProviderService = this.props.pageContext.getService<ISourceProviderService>("ISourceProviderService");
        return sourceProviderService.getSourceProvider(repositoryResource.type);
    }

    private async _initializeResources() {
        if (!this.props.store.runParametersAreLoaded) {
            this._isLoading.value = true;
            await this.props.store.fetchPipelineResources();
            this._isLoading.value = false;
        }
        this._updateResources();
        this.props.store.repositoryResources && this.props.store.repositoryResources.subscribe(this._updateResources);
        this.props.store.pipelineResources && this.props.store.pipelineResources.subscribe(this._updateResources);
        this.props.store.buildResources && this.props.store.buildResources.subscribe(this._updateResources);
        this.props.store.containerResources && this.props.store.containerResources.subscribe(this._updateResources);
        this.props.store.packageResources && this.props.store.packageResources.subscribe(this._updateResources);
    }

    private _updateResources = (): void => {
        const resources: IResourceItem[] = [];

        if (this.props.store.repositoryResources) {
            const repositories: IResourceItem[] = this.props.store.repositoryResources.value
                .filter(repository => !equals(repository.alias, "self", true));
            resources.push(...repositories);
        }

        if (this.props.store.pipelineResources) {
            const pipelines: IResourceItem[] = this.props.store.pipelineResources.value;
            resources.push(...pipelines);
        }

        if (this.props.store.buildResources) {
            const builds: IResourceItem[] = this.props.store.buildResources.value;
            resources.push(...builds);
        }

        if (this.props.store.containerResources) {
            const containers: IResourceItem[] = this.props.store.containerResources.value;
            resources.push(...containers);
        }

        if (this.props.store.packageResources) {
            const packages: IResourceItem[] = this.props.store.packageResources.value;
            resources.push(...packages);
        }

        this._resources.value = resources;
    }

    private _getUpdatedRefTextAndIcon(ref: string): [string, string] {
        if (ref.startsWith(GitConstants.RefsHeadsPrefix)) {
            return [ref.substr(GitConstants.RefsHeadsPrefix.length), "OpenSource"];
        }
        else if (ref.startsWith(GitConstants.RefsTagsPrefix)) {
            return [ref.substr(GitConstants.RefsTagsPrefix.length), "Tag"];
        }
        else if (ref.startsWith(GitConstants.RefsPrefix)) {
            return [ref.substr(GitConstants.RefsPrefix.length), "OpenSource"];
        }
        else {
            return [ref.substr(0, GitConstants.ShortenedCommitHashLength), "BranchCommit"];
        }
    }

    private get layoutManager(): IVssLayoutManager {
        return this.props.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
    }
}
